
// src/pages/modules/BreakTimeDeviationReportPage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBreakTimeDeviationReport,
  exportBreakTimeDeviationReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchEmployeeDropdown } from 'store/slices/commonSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Clock, User, AlertTriangle, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import FormField from 'components/common/FormField';
import { formatDateTime } from 'utlis/formateDateTime';

const BreakTimeDeviationReportPage = () => {
  const dispatch = useDispatch();

  const {
    breakTimeDeviationReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  // Get employees from Redux state
  const employees = useSelector((state) => state.common?.employees?.items || []);
  
  // Get loading state for employees
  const employeesLoading = useSelector((state) => state.common?.employees?.loading || false);

  const summary = breakTimeDeviationReport.data.summary || {};
  const results = breakTimeDeviationReport.data.results || [];
  const pagination = breakTimeDeviationReport.pagination || {};

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);
  
  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    employee: 'all',
    title: 'all',
    deviation_type: 'all',
    deviation_min: '',
    deviation_max: '',
    other: '',
  });

  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value => 
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'time_deviation',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.employee !== 'all' && { employee: advancedFilters.employee }),
      ...(advancedFilters.title !== 'all' && { title: advancedFilters.title }),
      ...(advancedFilters.deviation_type !== 'all' && { deviation_type: advancedFilters.deviation_type }),
      ...(advancedFilters.deviation_min && { deviation_min: advancedFilters.deviation_min }),
      ...(advancedFilters.deviation_max && { deviation_max: advancedFilters.deviation_max }),
      ...(advancedFilters.other && { other: advancedFilters.other }),
    };
    
    
    dispatch(fetchBreakTimeDeviationReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all employees on component mount
  useEffect(() => {
    dispatch(fetchEmployeeDropdown({}));
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      employee: 'all',
      title: 'all',
      deviation_type: 'all',
      deviation_min: '',
      deviation_max: '',
      other: '',
    });
  };

  const columns = [
    { accessorKey: 'employee_code', header: 'Employee Code' },
    {
      accessorKey: 'employee_name',
      header: 'Employee Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.employee_name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Break Title',
      cell: ({ row }) => (
        <span>{row.original.title || 'Untitled Break'}</span>
      ),
    },
    {
      accessorKey: 'punch_in',
      header: 'Punch In',
     cell: ({ row }) => (formatDateTime(row.original.punch_in) ?? '-')
    },
    {
      accessorKey: 'punch_out',
      header: 'Punch Out',
      cell: ({ row }) => (formatDateTime(row.original.punch_out) ?? '-')
    },
    // {
    //   accessorKey: 'duration',
    //   header: 'Duration',
    //   cell: ({ row }) => (
    //     <Badge variant={row.original.duration === '0m' ? 'outline' : 'default'}>
    //       {row.original.duration}
    //     </Badge>
    //   ),
    // },
    {
      accessorKey: 'deviation_type',
      header: 'Deviation Type',
      cell: ({ row }) => {
        const type = row.original.deviation_type;
        const variant = type === 'Late' ? 'destructive' : type === 'Early' ? 'default' : 'secondary';
        return (
          <Badge variant={variant} className="font-medium">
            {type || 'N/A'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'deviation_duration',
      header: 'Deviation Duration',
      cell: ({ row }) => (
        <Badge variant="outline" className="text-lg font-semibold">
          <Clock className="h-3 w-3 mr-1" />
          {row.original.deviation_duration}
        </Badge>
      ),
    },
    // {
    //   accessorKey: 'deviation_minutes',
    //   header: 'Deviation Minutes',
    //   cell: ({ row }) => (
    //     <span className="text-muted-foreground">{row.original.deviation_minutes}</span>
    //   ),
    // },
    // {
    //   accessorKey: 'deviation_reason',
    //   header: 'Deviation Reason',
    //   cell: ({ row }) => (
    //     <span className="text-sm text-muted-foreground">
    //       {row.original.deviation_reason || '-'}
    //     </span>
    //   ),
    // },
    // {
    //   accessorKey: 'other',
    //   header: 'Other',
    //   cell: ({ row }) => (
    //     <span className="text-sm text-muted-foreground">
    //       {row.original.other || '-'}
    //     </span>
    //   ),
    // },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Break Time Deviation Report"
        description="Track attendance policy violations and time deviations from standard schedule"
      />

      <LogDateFilter onFilter={(f) => setDateFilters(f)} />

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="employee">Employee</Label>
                <Select
                  value={advancedFilters.employee}
                  onValueChange={(value) => handleAdvancedFilterChange('employee', value)}
                  disabled={employeesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={employeesLoading ? "Loading..." : "Select employee"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.isArray(employees) && employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title">Break Title</Label>
                <Select
                  value={advancedFilters.title}
                  onValueChange={(value) => handleAdvancedFilterChange('title', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select title" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Lunch Break">Lunch Break</SelectItem>
                    <SelectItem value="Dinner Break">Dinner Break</SelectItem>
                    <SelectItem value="Tea Break">Tea Break</SelectItem>
                    <SelectItem value="Custom Break">Custom Break</SelectItem>
                    <SelectItem value="Not On Desk">Not On Desk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deviation_type">Deviation Type</Label>
                <Select
                  value={advancedFilters.deviation_type}
                  onValueChange={(value) => handleAdvancedFilterChange('deviation_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select deviation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Late">Late</SelectItem>
                    <SelectItem value="Early">Early</SelectItem>
                    <SelectItem value="Early Departure">Early Departure</SelectItem>
                    <SelectItem value="Late Arrival">Late Arrival</SelectItem>
                    <SelectItem value="Early Departure">Early Departure</SelectItem>
                    <SelectItem value="Early Break">Early Break</SelectItem>
                    <SelectItem value="Late Break">Late Break</SelectItem>
                    <SelectItem value="Late Departure">Late Departure</SelectItem>
                    <SelectItem value="Late Arrival">Late Arrival</SelectItem>
                    <SelectItem value="Unauthorized Break">Unauthorized Break</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              <FormField
  label="Employee"
  name="employee"
  type="select"
  value={advancedFilters.employee}
  onChange={(e) =>
    handleAdvancedFilterChange('employee', e.target.value)
  }
  disabled={employeesLoading}
  options={[
    { value: 'all', label: 'All' },
    ...(Array.isArray(employees)
      ? employees.map((employee) => ({
          value: String(employee.id),
          label: employee.name,
        }))
      : []),
  ]}
/>

<FormField
  label="Break Title"
  name="title"
  type="select"
  value={advancedFilters.title}
  onChange={(e) =>
    handleAdvancedFilterChange('title', e.target.value)
  }
  options={[
    { value: 'all', label: 'All' },
    { value: 'morning_break', label: 'Morning Break' },
    { value: 'lunch_break', label: 'Lunch Break' },
    { value: 'evening_break', label: 'Evening Break' },
    { value: 'dinner_break', label: 'Dinner Break' },
    { value: 'not_on_desk_break', label: 'Not On Desk Break' },
    { value: 'attendance', label: 'Attendance' },
  ]}
/>

<FormField
  label="Deviation Type"
  name="deviation_type"
  type="select"
  value={advancedFilters.deviation_type}
  onChange={(e) =>
    handleAdvancedFilterChange('deviation_type', e.target.value)
  }
  options={[
    { value: 'all', label: 'All' },
    // { value: 'Late', label: 'Late' },
    // { value: 'Early', label: 'Early' },
    { value: 'Late Arrival', label: 'Late Arrival' },
    { value: 'Early Departure', label: 'Early Departure' },
    { value: 'Early Break', label: 'Early Break' },
    { value: 'Late Break', label: 'Late Break' },
    { value: 'Late Departure', label: 'Late Departure' },
    // { value: 'Unauthorized Break', label: 'Unauthorized Break' },
    // { value: 'Other', label: 'Other' },
  ]}
/>

              
              <div className="space-y-2">
                <Label>Deviation Range (minutes)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={advancedFilters.deviation_min}
                    onChange={(e) => handleAdvancedFilterChange('deviation_min', e.target.value)}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={advancedFilters.deviation_max}
                    onChange={(e) => handleAdvancedFilterChange('deviation_max', e.target.value)}
                  />
                </div>
              </div>
              
              {/* <div className="space-y-2">
                <Label htmlFor="other">Other Reason</Label>
                <Input
                  placeholder="Other reason"
                  value={advancedFilters.other}
                  onChange={(e) => handleAdvancedFilterChange('other', e.target.value)}
                />
              </div> */}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deviation Entries</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{pagination.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deviation Time</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary.total_deviation_time || '0m'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Late Deviations</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {summary.late_deviation_count || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Early Deviations</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary.early_deviation_count || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading break time deviation data...</span>
        </div>
      ) : ( */}
        <DataTable
          data={results} // Use the paginated results from the server
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by employee code, name..."
          onSearch={setSearchTerm}
          showSearch={true}
          pageIndex={page - 1} // Convert to 0-based index for DataTable
          pageSize={pageSize}
          pageCount={totalPages} // Use total pages from server response
          onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
          onPageSizeChange={(newSize) => setPageSize(newSize)}
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportBreakTimeDeviationReport({ 
              filters: { 
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'break_time_deviation',
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.employee !== 'all' && { employee: advancedFilters.employee }),
                ...(advancedFilters.title !== 'all' && { title: advancedFilters.title }),
                ...(advancedFilters.deviation_type !== 'all' && { deviation_type: advancedFilters.deviation_type }),
                ...(advancedFilters.deviation_min && { deviation_min: advancedFilters.deviation_min }),
                ...(advancedFilters.deviation_max && { deviation_max: advancedFilters.deviation_max }),
                ...(advancedFilters.other && { other: advancedFilters.other }),
              } 
            }))
          }
          showExport={true}
          showImport={false}
        />
      {/* )} */}
    </div>
  );
};

export default BreakTimeDeviationReportPage;